import React from 'react';
import { primitives } from '@lexody/design-tokens';
import { FunButton, Lottie, Toast, gradient } from './GameUI';

const URGENT_SECONDS = 30;
const SCORE_DELAY_MS = 1000;

const DESCRIBER_COLORS = [primitives.violet['600'], primitives.pink['600']];
const GUESSER_COLORS = [primitives.blue['600'], primitives.violet['600']];

function formatClock(seconds) {
  if (seconds === null || seconds === undefined) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s < 10 ? `0${s}` : s}`;
}

function partnerName(session, game) {
  const partner = game.my_role === 'host' ? session.guest : session.host;
  return (partner && (partner.nickname || partner.first_name)) || 'Your partner';
}

class GamePoints extends React.Component {
  state = { score: this.props.score };

  componentDidUpdate(prevProps) {
    if (prevProps.score !== this.props.score) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.setState({ score: this.props.score });
        if (this.burst) this.burst.play();
      }, SCORE_DELAY_MS);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  render() {
    return (
      <div className="gpPoints" aria-label={`Team score ${this.state.score}`}>
        <span className="gpPoints__star" aria-hidden="true">⭐</span>
        <span className="gpPoints__value">{this.state.score}</span>
        <Lottie
          ref={(el) => { this.burst = el; }}
          src={this.props.lottie}
          autoplay={false}
          className="gpPoints__burst"
        />
      </div>
    );
  }
}

const GameTimer = ({ text, urgent }) => (
  <div
    className={`gpTimer${urgent ? ' gpTimer--urgent' : ''}`}
    role="timer"
    aria-label={`Time left ${text}`}
  >
    <span aria-hidden="true">{urgent ? '🔥' : '⏱️'}</span>
    <span className="gpTimer__value">{text}</span>
  </div>
);

const PracticeTimer = ({ disabled, onEnd }) => (
  <div className="gpTimer gpTimer--practice">
    <span aria-hidden="true">🧪</span>
    <span className="gpTimer__value">Practice</span>
    <button
      type="button"
      className="gpTimer__end"
      onClick={onEnd}
      disabled={disabled}
      aria-label="End practice"
    >
      End ✓
    </button>
  </div>
);

const GameSquare = ({ square, disabled, onGuess, wrongLottie }) => (
  <button
    type="button"
    className={`gpSquare${square.wrong ? ' gpSquare--wrong' : ''}`}
    onClick={() => onGuess(square.position)}
    disabled={disabled || square.wrong}
    aria-label={square.wrong ? 'Wrong picture' : `Picture ${square.position + 1}`}
  >
    <img className="gpSquare__image" src={square.image_url} alt="" draggable={false} />
    {square.wrong && <Lottie src={wrongLottie} className="gpSquare__wrong" />}
  </button>
);

const STYLE_FALLBACK = { original: 'large', large: 'medium' };

function fallbackPictureUrl(url) {
  if (!url) return null;
  const match = url.match(/\/(original|large)\//);
  if (!match) return null;
  return url.replace(`/${match[1]}/`, `/${STYLE_FALLBACK[match[1]]}/`);
}

const PromptSquare = ({ imageUrl }) => {
  const [src, setSrc] = React.useState(imageUrl);

  React.useEffect(() => {
    setSrc(imageUrl);
  }, [imageUrl]);

  return (
    <div className="gpPrompt">
      <img
        className="gpPrompt__image"
        src={src}
        alt="Your secret picture"
        draggable={false}
        onError={() => {
          const next = fallbackPictureUrl(src);
          if (next) setSrc(next);
        }}
      />
    </div>
  );
};

const PracticePrompt = ({ title, text }) => (
  <div className="gpPractice">
    <span className="gpPractice__pulse" aria-hidden="true" />
    <span className="gpPractice__emoji" aria-hidden="true">🧪</span>
    <div className="gpPractice__text">
      <p className="gpPractice__title">{title}</p>
      <p className="gpPractice__body">{text}</p>
    </div>
  </div>
);

class MatchingGame extends React.Component {
  state = { tipsOpen: false };

  feedback() {
    const { session } = this.props;
    const game = session.game;
    const result = game.last_result;
    if (!result || result.round !== session.round || result.outcome !== 'wrong') {
      return null;
    }
    return {
      key: `${result.round}:${result.picture_id}`,
      text: game.i_am_describer
        ? `${partnerName(session, game)} picked the wrong picture. Try describing it another way!`
        : 'Not that one. Listen again and try another picture.',
    };
  }

  renderRole() {
    const { session } = this.props;
    const game = session.game;
    const language = session.language ? session.language.language : 'the game language';
    const role = game.i_am_describer
      ? { emoji: '🗣️', title: 'You describe', colors: DESCRIBER_COLORS }
      : { emoji: '👂', title: 'You guess', colors: GUESSER_COLORS };
    const showTips = !game.practice && this.state.tipsOpen;

    return (
      <section className="gpRole" style={{ background: gradient(role.colors) }}>
        <div className="gpRole__row">
          <span className="gpRole__emoji" aria-hidden="true">{role.emoji}</span>
          <div className="gpRole__text">
            <p className="gpRole__title">{role.title}</p>
            <p className="gpRole__language">Speak only in {language}</p>
          </div>
          {!game.practice && (
            <button
              type="button"
              className="gpRole__tips"
              onClick={() => this.setState(({ tipsOpen }) => ({ tipsOpen: !tipsOpen }))}
              aria-expanded={showTips}
            >
              {showTips ? 'Hide' : '💡 Tips'}
            </button>
          )}
        </div>
        {showTips && (
          <div className="gpRole__tipsBody">
            {game.i_am_describer && <p className="gpRole__secret">🤫 Only you can see this picture.</p>}
            <p className="gpRole__instruction">{game.instruction}</p>
          </div>
        )}
      </section>
    );
  }

  renderPractice() {
    const game = this.props.session.game;
    if (!game.practice) return null;
    return (
      <PracticePrompt
        title={`Practice ${game.practice_round} of ${game.practice_rounds}`}
        text={game.instruction}
      />
    );
  }

  renderBoard() {
    const { session, lotties } = this.props;
    const game = session.game;

    if (this.props.showCorrect) {
      return <Lottie src={lotties.correct} className="gpCorrect" />;
    }

    if (game.i_am_describer) {
      return game.secret_picture ? <PromptSquare imageUrl={game.secret_picture.image_url} /> : null;
    }

    return (
      <div className="gpGrid">
        {game.board.map((square) => (
          <GameSquare
            key={`${session.round}-${square.position}`}
            square={square}
            disabled={this.props.busy}
            onGuess={this.props.onGuess}
            wrongLottie={lotties.wrong}
          />
        ))}
      </div>
    );
  }

  render() {
    const { session, secondsRemaining, lotties } = this.props;
    const game = session.game;
    const feedback = this.feedback();
    const urgent = !game.practice && secondsRemaining !== null && secondsRemaining <= URGENT_SECONDS;
    const rows = game.i_am_describer ? 1 : Math.max(1, Math.ceil(game.board.length / 3));

    return (
      <div className="gpPlay">
        <div className="gpPlay__side">
          <div className="gpHud">
            <GamePoints score={game.team_score} lottie={lotties.score} />
            {game.practice
              ? <PracticeTimer disabled={this.props.busy} onEnd={this.props.onEndPractice} />
              : <GameTimer text={formatClock(secondsRemaining)} urgent={urgent} />}
          </div>

          {this.renderRole()}
          {this.renderPractice()}

          <FunButton
            variant="ghost"
            emoji="🏁"
            text="End game"
            onClick={this.props.onEndGame}
            className="gpPlay__end"
          />
        </div>

        <div className="gpBoard" style={{ '--gp-rows': rows }}>
          {this.renderBoard()}
        </div>

        {feedback && <Toast key={feedback.key} emoji="😅" text={feedback.text} />}
        {this.props.error !== '' && <Toast key={this.props.error} emoji="😬" text={this.props.error} />}
      </div>
    );
  }
}

export default MatchingGame;
